import type { CustomFieldDefinition } from '../types/custom-field-definitions.types';
import { cn } from '@/lib/utils';
import { getCustomFieldDefinitionsForColumns } from '../utils/custom-field-attributes.utils';

export const CUSTOM_ATTRIBUTE_COLUMN_PREFIX = 'attr:';

export const CONTACTS_TABLE_CHECKBOX_STICKY_CLASS =
  'sticky left-0 z-20 w-11 min-w-11 max-w-11 bg-neutral-50';

export const CONTACTS_TABLE_NAME_STICKY_CLASS =
  'sticky left-11 z-20 shadow-[inset_-1px_0_0_0_#E7E8EA]';

export const REQUIRED_CONTACTS_TABLE_COLUMNS = ['name', 'email'] as const;

export type StaticContactsTableColumnId =
  | 'name'
  | 'email'
  | 'lists'
  | 'phone'
  | 'source'
  | 'created'
  | 'language';

export type ContactsTableColumnId =
  | StaticContactsTableColumnId
  | `${typeof CUSTOM_ATTRIBUTE_COLUMN_PREFIX}${string}`;

export interface ContactsTableColumnDef {
  id: ContactsTableColumnId;
  label: string;
  widthClass: string;
  canHide: boolean;
  defaultVisible: boolean;
  attributeKey?: string;
}

export const STATIC_CONTACTS_TABLE_COLUMNS: ContactsTableColumnDef[] = [
  {
    id: 'name',
    label: 'Name',
    widthClass: 'min-w-[12rem] w-[12rem]',
    canHide: false,
    defaultVisible: true,
  },
  {
    id: 'email',
    label: 'Email',
    widthClass: 'min-w-[14rem] w-[14rem]',
    canHide: false,
    defaultVisible: true,
  },
  {
    id: 'phone',
    label: 'Phone',
    widthClass: 'min-w-[9rem] w-[9rem]',
    canHide: true,
    defaultVisible: true,
  },
  {
    id: 'source',
    label: 'Source',
    widthClass: 'min-w-[9rem] w-[9rem]',
    canHide: true,
    defaultVisible: true,
  },
  {
    id: 'lists',
    label: 'Lists',
    widthClass: 'min-w-[10rem] w-[10rem]',
    canHide: true,
    defaultVisible: true,
  },
  {
    id: 'created',
    label: 'Created',
    widthClass: 'min-w-[8rem] w-[8rem]',
    canHide: true,
    defaultVisible: true,
  },
  {
    id: 'language',
    label: 'Language',
    widthClass: 'min-w-[8rem] w-[8rem]',
    canHide: true,
    defaultVisible: false,
  },
];

export const buildCustomAttributeColumnId = (key: string): ContactsTableColumnId =>
  `${CUSTOM_ATTRIBUTE_COLUMN_PREFIX}${key}`;

export const parseCustomAttributeColumnKey = (
  columnId: ContactsTableColumnId
): string | undefined => {
  if (!columnId.startsWith(CUSTOM_ATTRIBUTE_COLUMN_PREFIX)) return undefined;
  const key = columnId.slice(CUSTOM_ATTRIBUTE_COLUMN_PREFIX.length);
  return key || undefined;
};

export const isCustomAttributeColumnId = (
  columnId: ContactsTableColumnId
): columnId is `${typeof CUSTOM_ATTRIBUTE_COLUMN_PREFIX}${string}` =>
  columnId.startsWith(CUSTOM_ATTRIBUTE_COLUMN_PREFIX);

export const buildCustomAttributeColumns = (
  definitions: CustomFieldDefinition[] = []
): ContactsTableColumnDef[] =>
  getCustomFieldDefinitionsForColumns(definitions).map((definition) => ({
    id: buildCustomAttributeColumnId(definition.Key),
    label: definition.Label,
    widthClass: 'min-w-[10rem] w-[10rem]',
    canHide: true,
    defaultVisible: false,
    attributeKey: definition.Key,
  }));

export const buildContactsTableColumns = (
  definitions: CustomFieldDefinition[] = []
): ContactsTableColumnDef[] => [
  ...STATIC_CONTACTS_TABLE_COLUMNS,
  ...buildCustomAttributeColumns(definitions),
];

/** @deprecated Use REQUIRED_CONTACTS_TABLE_COLUMNS */
export const REQUIRED_CONTACTS_TABLE_COLUMN: ContactsTableColumnId = 'name';

export const buildDefaultContactsColumnOrder = (
  definitions: CustomFieldDefinition[] = []
): ContactsTableColumnId[] => buildContactsTableColumns(definitions).map((column) => column.id);

export const DEFAULT_CONTACTS_COLUMN_ORDER: ContactsTableColumnId[] =
  buildDefaultContactsColumnOrder();

export const buildContactsTableColumnMap = (
  definitions: CustomFieldDefinition[] = []
): Record<ContactsTableColumnId, ContactsTableColumnDef> =>
  Object.fromEntries(
    buildContactsTableColumns(definitions).map((column) => [column.id, column])
  ) as Record<ContactsTableColumnId, ContactsTableColumnDef>;

export const CONTACTS_TABLE_COLUMN_MAP = buildContactsTableColumnMap();

export const buildDefaultContactsColumnVisibility = (
  definitions: CustomFieldDefinition[] = []
): Record<ContactsTableColumnId, boolean> =>
  Object.fromEntries(
    buildContactsTableColumns(definitions).map((column) => [column.id, column.defaultVisible])
  ) as Record<ContactsTableColumnId, boolean>;

export const DEFAULT_CONTACTS_COLUMN_VISIBILITY: Record<ContactsTableColumnId, boolean> =
  buildDefaultContactsColumnVisibility();

export const CONTACTS_TABLE_COLUMN_STORAGE_KEY = 'contacts-table-column-config';

export const getContactsTableHeaderCellClass = (
  columnId: ContactsTableColumnId,
  widthClass: string
) =>
  cn(
    'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 text-xs font-semibold text-neutral-600',
    widthClass,
    columnId === 'name' && CONTACTS_TABLE_NAME_STICKY_CLASS
  );

export const getContactsTableBodyCellClass = (
  columnId: ContactsTableColumnId,
  widthClass: string,
  options?: { selected?: boolean }
) =>
  cn(
    'truncate whitespace-nowrap px-4 py-3 align-middle',
    widthClass,
    options?.selected ? 'bg-primary-50' : 'bg-white group-hover:bg-neutral-50',
    columnId === 'name' && CONTACTS_TABLE_NAME_STICKY_CLASS
  );
